import React from 'react';

export type ShortcutVariant = 'light' | 'dark' | 'subtle';

const KBD_BASE =
  'inline-flex items-center align-middle rounded border px-1 py-0.5 font-mono leading-none';

export const KBD_CLASS: Record<ShortcutVariant, string> = {
  /** White bg, charcoal text — use on non-white/dark parent backgrounds. */
  light: `${KBD_BASE} border-gray-medium bg-white text-charcoal`,
  /** Charcoal bg, white text — use on light/white parent backgrounds. */
  dark: `${KBD_BASE} border-charcoal bg-charcoal text-white`,
  /** Outlined, muted text — for hints beside a control's own label, where a filled keycap would compete with it. */
  subtle: `${KBD_BASE} border-gray-medium bg-transparent text-gray-muted`,
};

/**
 * Outer span is sized at 1em × 1em (relative to the parent kbd's font-size),
 * locking the badge to the same height as other keys. The inner span bumps the
 * ⌘ glyph to 1.25em so it looks visually comparable to the surrounding text
 * while staying vertically centred within the fixed container.
 * cmd-symbol switches to a non-mono font so justify-center can actually centre
 * the glyph (in font-mono every char fills exactly 1em, leaving no room to shift).
 */
export const CmdKey = () => (
  <span className="cmd-symbol inline-flex h-[1em] w-[1em] items-center justify-center">
    <span className="text-[1.25em] leading-none">⌘</span>
  </span>
);

/** The Mac Option glyph, sized like CmdKey. */
export const OptionKey = () => (
  <span className="cmd-symbol inline-flex h-[1em] w-[1em] items-center justify-center">
    <span className="text-[1.25em] leading-none">⌥</span>
  </span>
);

interface KeyProps {
  children: React.ReactNode;
  variant?: ShortcutVariant;
}

/** Single keycap badge. Use when you need individual key labels in prose. */
export const ShortcutKey: React.FC<KeyProps> = ({
  children,
  variant = 'dark',
}) => <kbd className={KBD_CLASS[variant]}>{children}</kbd>;

interface Props {
  shortcutKey: string;
  /**
   * 'light' — white bg, black text, border. Use on non-white/dark parent backgrounds.
   * 'dark'  — dark bg, white text, border. Use on light/white parent backgrounds.
   * 'subtle' — outlined, muted text. Use beside a control's own label.
   * Defaults to 'dark'.
   */
  variant?: ShortcutVariant;
  /**
   * The modifier: Ctrl / ⌘ (default), or Alt / ⌥ — for shortcuts that would
   * otherwise clash with the browser's own Ctrl / ⌘ ones (OP-2556).
   */
  modifier?: 'ctrlCmd' | 'altOption';
}

const ShortcutHint = ({
  shortcutKey,
  variant = 'dark',
  modifier = 'ctrlCmd',
}: Props) => {
  const kbd = KBD_CLASS[variant];
  const isAlt = modifier === 'altOption';
  return (
    <span className="inline-flex items-center gap-0.5 text-xs">
      <kbd className={kbd}>{isAlt ? 'Alt' : 'Ctrl'}</kbd>
      <span>/</span>
      <kbd className={kbd}>{isAlt ? <OptionKey /> : <CmdKey />}</kbd>
      <span>+</span>
      <kbd className={kbd}>{shortcutKey}</kbd>
    </span>
  );
};

export default ShortcutHint;
